import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Button,
  Collapse,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Assignment as AssignmentIcon,
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  AssignmentTurnedIn as AssignIcon,
  PlaylistAddCheck as AcceptCaseIcon,
  Description as ViewDetailsIcon,
  AssignmentTurnedIn as ReleaseCaseIcon,
  SaveAlt as SaveIcon,
  CheckCircle as SuccessIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const PolicyReviewPage = () => {
  const navigate = useNavigate();
  const [isGuideExpanded, setIsGuideExpanded] = useState(false);
  const [selectedPolicies, setSelectedPolicies] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  
  // 模拟当前登录的核保人
  const currentUnderwriter = {
    id: 1,
    name: 'Sarah Wilson',
    avatar: 'SW',
    color: '#1976d2'
  };

  // 修改为单一的 policies 数组
  const [policies, setPolicies] = useState([
    {
      id: 1,
      policyNumber: 'POL-2024-001',
      type: 'Auto Insurance',
      submittedBy: 'John Smith',
      submittedDate: '2024-01-25',
      complexity: 'medium',
      status: 'pending_review',
      assignedTo: null
    },
    {
      id: 2,
      policyNumber: 'POL-2024-002',
      type: 'Home Insurance',
      submittedBy: 'Emma Wilson',
      submittedDate: '2024-01-24',
      complexity: 'low',
      status: 'pending_review',
      assignedTo: null
    },
    {
      id: 3,
      policyNumber: 'POL-2024-003',
      type: 'Life Insurance',
      submittedBy: 'Michael Brown',
      submittedDate: '2024-01-23',
      complexity: 'high',
      status: 'pending_review',
      assignedTo: null
    }
  ]);

  // 处理选择政策
  const handleSelectPolicy = (policyId) => {
    setSelectedPolicies(prev => {
      if (prev.includes(policyId)) {
        return prev.filter(id => id !== policyId);
      } else {
        return [...prev, policyId];
      }
    });
  };

  // 修改保存到列表的处理函数
  const handleSaveToMyList = () => {
    // 这里可以添加保存到后端的逻辑
    setOpenDialog(true);
  };

  // 处理继续选择
  const handleContinue = () => {
    setOpenDialog(false);
    setSelectedPolicies([]); // 清空选择
  };

  // 处理跳转到列表
  const handleGoToList = () => {
    navigate('/underwriter/approved');
  };

  const COMPLEXITY_LEVELS = {
    low: {
      icon: <CheckCircleIcon fontSize="small" />,
      color: 'success',
      label: 'Simple',
      description: [
        'No accidents in past 6 years',
        'No traffic tickets',
        'No out-of-province driving experience'
      ]
    },
    medium: {
      icon: <WarningIcon fontSize="small" />,
      color: 'warning',
      label: 'Moderate',
      description: [
        'Has accidents or traffic tickets',
        'No out-of-province driving experience',
        'Standard documentation'
      ]
    },
    high: {
      icon: <ErrorIcon fontSize="small" />,
      color: 'error',
      label: 'Complex',
      description: [
        'Multiple accidents or tickets',
        'Out-of-province driving experience',
        'Letter of Experience (LOE) required',
        'Complex documentation review needed'
      ]
    }
  };

  const STATUS_CONFIG = {
    pending_review: { label: 'Pending Review', color: 'warning' },
    in_review: { label: 'In Review', color: 'info' },
    approved: { label: 'Approved', color: 'success' },
    rejected: { label: 'Rejected', color: 'error' }
  };

  // 获取复杂度标签
  const getComplexityChip = (complexity) => {
    const config = COMPLEXITY_LEVELS[complexity];
    return (
      <Tooltip title={
        <Box sx={{ p: 1 }}>
          <Typography variant="subtitle2">{config.label} Case:</Typography>
          <ul style={{ paddingLeft: '20px', margin: '8px 0 0 0' }}>
            {config.description.map((line, index) => (
              <li key={index} style={{ fontSize: '0.875rem' }}>{line}</li>
            ))}
          </ul>
        </Box>
      }>
        <Chip
          icon={config.icon}
          label={config.label}
          size="small"
          color={config.color}
          variant="outlined"
        />
      </Tooltip>
    );
  };

  // 获取状态标签
  const getStatusChip = (status) => {
    const config = STATUS_CONFIG[status];
    return (
      <Chip 
        label={config.label}
        color={config.color}
        size="small"
        sx={{ 
          fontWeight: 500,
          '& .MuiChip-label': { px: 2 }
        }}
      />
    );
  };

  // 创建可复用表格组件
  const PolicyTable = ({ title, policies, showAssign }) => {
    const isAvailableTable = title.includes("Available");

    // 获取操作列标题
    const getActionColumnTitle = () => {
      if (isAvailableTable) {
        return "Take Case";
      }
      return "Release Case";
    };

    return (
      <Paper elevation={3} sx={{ 
        mb: 4, 
        border: '1px solid rgba(0, 0, 0, 0.08)',
        borderRadius: 2,
        background: 'linear-gradient(to bottom, #ffffff, #fafafa)'
      }}>
        {/* 标题区域 */}
        <Box sx={{ 
          p: 2.5,
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          background: 'linear-gradient(to right, #f8f9fa, #ffffff)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderTopLeftRadius: 2,
          borderTopRightRadius: 2,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" sx={{ 
              color: '#1976d2',
              fontWeight: 600,
              fontSize: '1.1rem'
            }}>
              {title}
            </Typography>
            <Chip 
              label={`${policies.length} cases`} 
              sx={{ 
                background: 'linear-gradient(45deg, #1976d2, #2196f3)',
                color: '#fff',
                fontWeight: 500,
                '& .MuiChip-label': {
                  textShadow: '0 1px 2px rgba(0,0,0,0.1)'
                }
              }}
              size="small"
            />
          </Box>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, width: '15%' }}>Policy Number</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '15%' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '15%' }}>Client</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '15%' }}>Submitted Date</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '15%' }}>Complexity</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '15%' }}>Status</TableCell>
                {/* 只在我的案件列表中显示 Review 列 */}
                {!isAvailableTable && (
                  <TableCell sx={{ fontWeight: 600, width: '10%' }}>Review</TableCell>
                )}
                <TableCell sx={{ fontWeight: 600, width: '10%' }}>
                  {getActionColumnTitle()}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {policies.length === 0 ? (
                <TableRow>
                  <TableCell 
                    colSpan={isAvailableTable ? 7 : 8} 
                    sx={{ 
                      p: 4, 
                      textAlign: 'center', 
                      color: 'text.secondary',
                      borderBottom: 'none'
                    }}
                  >
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center',
                      gap: 1
                    }}>
                      {isAvailableTable ? (
                        <AssignmentIcon sx={{ fontSize: 40, color: '#bdbdbd' }} />
                      ) : (
                        <AssignIcon sx={{ fontSize: 40, color: '#bdbdbd' }} />
                      )}
                      <Typography>No cases available</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                policies.map(policy => (
                  <TableRow 
                    key={policy.id}
                    sx={{ 
                      '&:hover': { 
                        bgcolor: 'rgba(25, 118, 210, 0.04)',
                        transition: 'background-color 0.2s'
                      }
                    }}
                  >
                    <TableCell>{policy.policyNumber}</TableCell>
                    <TableCell>{policy.type}</TableCell>
                    <TableCell>{policy.submittedBy}</TableCell>
                    <TableCell>{policy.submittedDate}</TableCell>
                    <TableCell>{getComplexityChip(policy.complexity)}</TableCell>
                    <TableCell>{getStatusChip(policy.status)}</TableCell>
                    
                    {/* 只在我的案件列表中显示查看按钮 */}
                    {!isAvailableTable && (
                      <TableCell>
                        <Tooltip title="Review Case Details" arrow placement="top">
                          <IconButton
                            onClick={() => navigate(`/underwriter/policy-review/${policy.id}`)}
                            sx={{
                              color: '#1976d2',
                              '&:hover': {
                                background: 'linear-gradient(45deg, rgba(25, 118, 210, 0.08), rgba(33, 150, 243, 0.08))',
                                transform: 'translateY(-1px)'
                              },
                              transition: 'all 0.2s'
                            }}
                          >
                            <ViewDetailsIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}

                    {/* 认领/取消认领按钮 */}
                    <TableCell>
                      <Tooltip 
                        title={isAvailableTable ? "Claim Case Ownership" : "Release Case"} 
                        arrow 
                        placement="top"
                      >
                        <IconButton
                          onClick={(e) => handleSelectPolicy(policy.id)}
                          sx={{
                            color: isAvailableTable ? '#2e7d32' : '#d32f2f',
                            '&:hover': {
                              background: isAvailableTable
                                ? 'linear-gradient(45deg, rgba(46, 125, 50, 0.08), rgba(76, 175, 80, 0.08))'
                                : 'linear-gradient(45deg, rgba(211, 47, 47, 0.08), rgba(244, 67, 54, 0.08))',
                              transform: 'translateY(-1px)'
                            },
                            transition: 'all 0.2s'
                          }}
                        >
                          {isAvailableTable ? <AcceptCaseIcon /> : <ReleaseCaseIcon />}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  };

  const ComplexityGuide = ({ isExpanded, onToggle }) => {
    return (
      <Paper elevation={0} sx={{ 
        p: 2, 
        mb: 4, 
        border: '1px solid #e0e0e0',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: '#f8f9fa'
        }
      }}>
        <Box 
          onClick={onToggle}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <IconButton size="small" sx={{ mr: 1 }}>
            <ExpandMoreIcon sx={{ 
              transform: isExpanded ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.3s'
            }}/>
          </IconButton>
          <Typography variant="h6" sx={{ color: '#1976d2' }}>
            Case Complexity Classification
          </Typography>
        </Box>
        
        <Collapse in={isExpanded}>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            {Object.entries(COMPLEXITY_LEVELS).map(([key, config]) => (
              <Grid item xs={12} md={4} key={key}>
                <Box sx={{ 
                  p: 2,
                  borderLeft: `4px solid ${config.color === 'success' ? '#4caf50' : 
                                        config.color === 'warning' ? '#ffb74d' : '#f44336'}`,
                  bgcolor: '#fff',
                  borderRadius: 1,
                  boxShadow: 1
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Chip 
                      label={config.label} 
                      size="small" 
                      color={config.color} 
                      variant="outlined"
                      sx={{ mr: 1.5 }}
                    />
                    <Typography variant="subtitle2">
                      {key === 'low' ? 'Low Risk Profile' : 
                       key === 'medium' ? 'Standard Risk' : 'High Complexity'}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {config.description.map(desc => (
                      <React.Fragment key={desc}>
                        • {desc}<br />
                      </React.Fragment>
                    ))}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Collapse>
      </Paper>
    );
  };

  return (
    <Container maxWidth="xl">
      {/* Hero Section */}
      <Box sx={{ 
        pt: 4,
        pb: 3,
        textAlign: 'center',
        position: 'relative',
        borderBottom: '1px solid #e0e0e0',
        mb: 4
      }}>
        <Button
          variant="outlined"
          onClick={() => navigate('/')}
          startIcon={<ArrowBackIcon />}
          sx={{
            position: 'absolute',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#1976d2',
            borderColor: '#1976d2',
            '&:hover': {
              borderColor: '#1565c0',
              backgroundColor: 'rgba(25, 118, 210, 0.04)'
            }
          }}
        >
          Back to Home
        </Button>

        <Typography 
          variant="h4" 
          sx={{ 
            color: '#1976d2',
            fontWeight: 600,
            mb: 1
          }}
        >
          Policy Review
        </Typography>
        <Typography 
          variant="subtitle1" 
          sx={{ 
            color: '#666',
            maxWidth: '600px',
            mx: 'auto'
          }}
        >
          Review and process insurance policy applications
        </Typography>
      </Box>

      {/* 复杂度分类指南 */}
      <ComplexityGuide 
        isExpanded={isGuideExpanded}
        onToggle={() => setIsGuideExpanded(!isGuideExpanded)}
      />

      {/* Available Cases Table */}
      <Paper elevation={3} sx={{ mb: 4 }}>
        <Box sx={{ 
          p: 2.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 600 }}>
              Available Cases for Assignment
            </Typography>
            <Chip 
              label={`${policies.length} cases`}
              sx={{ bgcolor: '#e3f2fd', color: '#1976d2' }}
            />
          </Box>
          {selectedPolicies.length > 0 && (
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveToMyList}
              sx={{
                bgcolor: '#1976d2',
                '&:hover': { bgcolor: '#1565c0' }
              }}
            >
              Save to My List ({selectedPolicies.length})
            </Button>
          )}
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell>Policy Number</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Submitted Date</TableCell>
                <TableCell>Complexity</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center" sx={{ width: '120px' }}>Select</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {policies.map((policy) => (
                <TableRow 
                  key={policy.id}
                  sx={{ 
                    '&:hover': { bgcolor: '#f8f9fa' },
                    transition: 'background-color 0.2s',
                    cursor: 'pointer'
                  }}
                  onClick={() => handleSelectPolicy(policy.id)}
                >
                  <TableCell>{policy.policyNumber}</TableCell>
                  <TableCell>{policy.type}</TableCell>
                  <TableCell>{policy.submittedBy}</TableCell>
                  <TableCell>{policy.submittedDate}</TableCell>
                  <TableCell>{getComplexityChip(policy.complexity)}</TableCell>
                  <TableCell>{getStatusChip(policy.status)}</TableCell>
                  <TableCell align="center">
                    <Checkbox
                      checked={selectedPolicies.includes(policy.id)}
                      sx={{
                        '& .MuiSvgIcon-root': {
                          fontSize: 28,
                          color: selectedPolicies.includes(policy.id) ? '#1976d2' : '#bdbdbd'
                        },
                        '&:hover': {
                          '& .MuiSvgIcon-root': {
                            color: '#1976d2'
                          }
                        }
                      }}
                      onClick={(e) => e.stopPropagation()} // 防止触发行点击事件
                      onChange={() => handleSelectPolicy(policy.id)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* 成功添加对话框 */}
      <Dialog
        open={openDialog}
        onClose={handleContinue}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          color: '#2e7d32'
        }}>
          <SuccessIcon sx={{ color: '#2e7d32' }} />
          Successfully Added to Your List
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {selectedPolicies.length} {selectedPolicies.length === 1 ? 'policy has' : 'policies have'} been added to your review list. What would you like to do next?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 2 }}>
          <Button
            variant="outlined"
            onClick={handleContinue}
            sx={{
              color: '#1976d2',
              borderColor: '#1976d2',
              '&:hover': {
                borderColor: '#1565c0',
                bgcolor: 'rgba(25, 118, 210, 0.04)'
              }
            }}
          >
            Continue Selecting
          </Button>
          <Button
            variant="contained"
            onClick={handleGoToList}
            sx={{
              bgcolor: '#1976d2',
              '&:hover': { bgcolor: '#1565c0' }
            }}
          >
            Go to My List
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PolicyReviewPage; 